import React from "react";
import { Box, Typography, Stack, Chip, alpha, keyframes, Divider } from "@mui/material";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import BlockIcon from "@mui/icons-material/Block";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";

/**
 * Persistent color/badge key for the timetable — grouped into
 * AVAILABILITY / CLASS TYPE / ACCOUNT so the eye lands on the right
 * category in ~1 second. Redesigned to a premium card that visually
 * belongs to the same page as the KPI + toolbar shells.
 *
 * All swatches remain visually identical to the actual grid chips
 * they explain (same colours, same shapes) — this legend is a KEY
 * to the schedule, not a decorative footer.
 */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

type Item = { label: string; swatch: React.ReactNode };

const GroupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    fontSize: 9.5, fontWeight: 800, letterSpacing: 1.1,
    color: "#475569", textTransform: "uppercase", mb: 0.75,
  }}>
    {children}
  </Typography>
);

const Row: React.FC<{ items: Item[] }> = ({ items }) => (
  <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
    {items.map((it) => (
      <Stack key={it.label} direction="row" spacing={0.75} alignItems="center">
        {it.swatch}
        <Typography sx={{ fontSize: 11.5, color: "#334155", fontWeight: 500 }}>
          {it.label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: alpha(color, 0.25), border: `2px solid ${color}` }} />
);

const Legend: React.FC = () => {
  const availability: Item[] = [
    { label: "Available Spot", swatch: <Dot color="#22c55e" /> },
    {
      label: "Spots pill (multiple)",
      swatch: (
        <Chip
          icon={<EventAvailableOutlinedIcon sx={{ fontSize: 12 }} />}
          label="2 Spots Available"
          size="small"
          sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#22c55e", 0.18), color: "#166534", fontWeight: 700, "& .MuiChip-icon": { ml: 0.4 } }}
        />
      ),
    },
    { label: "Full Class", swatch: <Chip label="Full" size="small" sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#EF5350", 0.15), color: "#b91c1c", fontWeight: 700 }} /> },
  ];

  const classType: Item[] = [
    {
      label: "Makeup",
      swatch: (
        <Chip
          icon={<AutorenewOutlinedIcon sx={{ fontSize: 12 }} />}
          label="Makeup"
          size="small"
          sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#F59E0B", 0.22), color: "#854d0e", fontWeight: 700, "& .MuiChip-icon": { ml: 0.4 } }}
        />
      ),
    },
    {
      label: "Online",
      swatch: (
        <Chip
          icon={<WifiOutlinedIcon sx={{ fontSize: 12 }} />}
          label="Online"
          size="small"
          sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#0ea5e9", 0.15), color: "#0369a1", fontWeight: 700, "& .MuiChip-icon": { ml: 0.4 } }}
        />
      ),
    },
    {
      label: "In-Person (colour per location)",
      swatch: (
        <Chip
          icon={<LocationOnOutlinedIcon sx={{ fontSize: 12 }} />}
          label="Location"
          size="small"
          sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#F97316", 0.15), color: "#9a3412", fontWeight: 700, "& .MuiChip-icon": { ml: 0.4 } }}
        />
      ),
    },
    {
      label: "Cancelled",
      swatch: (
        <Chip
          icon={<BlockIcon sx={{ fontSize: 12 }} />}
          label="Cancelled"
          size="small"
          sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#EF5350", 0.15), color: "#b91c1c", fontWeight: 700, "& .MuiChip-icon": { ml: 0.4 } }}
        />
      ),
    },
  ];

  const account: Item[] = [
    {
      label: "Outstanding invoice",
      swatch: (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            "& svg": {
              fontSize: 16,
              color: "#EF5350",
              animation: `${spin} 6s linear infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            },
          }}
        >
          <StarRateRoundedIcon />
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: "16px",
        bgcolor: "#fff",
        border: "1px solid rgba(15,23,42,0.06)",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{
        px: 2, pt: 1.25, pb: 0.75,
        borderBottom: "1px solid rgba(15,23,42,0.05)",
        background: "linear-gradient(180deg, #F8FAFC 0%, #ffffff 100%)",
      }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: 1,
          textTransform: "uppercase", color: "#475569",
        }}>
          Legend
        </Typography>
      </Box>
      <Box sx={{
        p: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 2fr 1fr" },
        gap: { xs: 2, md: 2.5 },
        alignItems: "start",
      }}>
        <Box>
          <GroupTitle>Availability</GroupTitle>
          <Row items={availability} />
        </Box>
        <Box sx={{ borderLeft: { md: "1px solid rgba(15,23,42,0.06)" }, pl: { md: 2.5 } }}>
          <GroupTitle>Class Type</GroupTitle>
          <Row items={classType} />
        </Box>
        <Box sx={{ borderLeft: { md: "1px solid rgba(15,23,42,0.06)" }, pl: { md: 2.5 } }}>
          <GroupTitle>Account</GroupTitle>
          <Row items={account} />
        </Box>
      </Box>
    </Box>
  );
};

// Silence unused-import warning while keeping the import list stable
// for future edits — `Divider` was previously used and is worth keeping
// available since the grouped layout may want a horizontal rule at
// small screens.
void Divider;

export default Legend;
